"use client";

import { LegalPageLayout } from "@/components/legal/legal-page-layout";
import { useTranslations } from "@/hooks/use-translations";

export default function PrivacyPage() {
  const { t } = useTranslations("legal");
  
  return (
    <LegalPageLayout title={t("privacyPolicyLabel")} lastUpdated="1 October 2026">
      <div className="prose prose-neutral max-w-none space-y-6 text-neutral-700">
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 mb-6">
            <p className="text-sm text-amber-900">
              <strong>{t("governingLanguage")}</strong>
            </p>
          </div>
          
          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">1. Who we are</h2>
            <p>
              GrowthSync is owned and operated by GrowthSync Network Ltd, a company registered in England and Wales (Company No. 16795645).
            </p>
            <p>
              Registered Office: Unit 6, Balfour Court, Leyland, United Kingdom, PR25 2TF
            </p>
            <p>
              Contact email: <a href="mailto:contact@growth-sync.com" className="text-blue-600 hover:text-blue-700 hover:underline">contact@growth-sync.com</a>
            </p>
            <p>
              GrowthSync is a business networking and events platform that allows users to create profiles, connect with others, and join or host events. We take your privacy seriously and are committed to protecting your personal data in line with the UK GDPR and Data Protection Act 2018.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">2. Information we collect</h2>
            <p>
              We collect and process the following types of personal data:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Account details:</strong> name, email address, password, company name, role, and profile information you choose to share.</li>
              <li><strong>Usage data:</strong> interactions within the app such as messages, posts, likes, or event participation.</li>
              <li><strong>Payment data:</strong> processed securely through our payment processor; we do not store full card details.</li>
              <li><strong>Technical data:</strong> device information, browser type, IP address, and usage analytics (for improving performance).</li>
              <li><strong>Location data:</strong> the city and country you are in. Where you allow it, we take a one off approximate reading of your device&rsquo;s position, accurate to around 11km, and match it to the nearest city; where you do not, we use the country your connection comes from. We never ask for or record your precise position. We store that city and country, and the coordinates of the city centre. We do not store your exact position, we do not track you over time, and we never collect your location in the background.</li>
            </ul>
            <p>
              We only collect data that is necessary to provide GrowthSync&rsquo;s services and functionality.
            </p>
            <p className="mt-4">
              Your location is used to set the city shown on your profile, so members can trust where
              other members are based, and to show you people, events and opportunities near you. The
              city and country appear on your profile and are visible to other members. Your
              coordinates are not shown to anyone. If your profile says one place and your device or
              connection says another, we show the one we found, so that locations on GrowthSync mean
              something.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">3. How we use your information</h2>
            <p>
              Your personal data is used to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Create and manage your user account.</li>
              <li>Display your profile and content to other users in accordance with your privacy settings.</li>
              <li>Allow event creation, RSVPs, and event management.</li>
              <li>Process payments and subscriptions.</li>
              <li>Send essential notifications (e.g., event reminders, messages, or system updates).</li>
              <li>Send marketing communications (only if you have consented; you can opt out at any time via your account settings or by contacting us).</li>
              <li>Improve, personalise, and secure our services.</li>
              <li>Comply with legal obligations and prevent fraud or misuse.</li>
            </ul>
            <p>
              If you participate in ambassador or referral programs, we may process limited data to track referral performance and reward eligibility.
            </p>
            <p>
              We do not sell or rent your data to third parties.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">4. Legal basis for processing</h2>
            <p>
              We process your data under one or more of the following lawful bases:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Performance of a contract</strong> – to provide the app's services and manage your account.</li>
              <li><strong>Legitimate interests</strong> – to improve and secure our services.</li>
              <li><strong>Consent</strong> – for optional marketing, email updates, or connecting third-party integrations.</li>
              <li><strong>Legal obligation</strong> – to comply with tax, financial, or law enforcement requirements.</li>
            </ul>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">5. Sharing your information</h2>
            <p>
              We may share your information with:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Trusted service providers who assist with platform operations, payment processing, and other essential services.</li>
              <li>Business partners or event organisers when you choose to interact with or join their events.</li>
              <li>Law enforcement or regulators if required by law.</li>
            </ul>
            <p>
              All partners and processors handle your data under strict confidentiality and data protection agreements.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">6. International data transfers</h2>
            <p>
              Data may be stored or processed using secure cloud providers located in the UK, EU, or EEA-equivalent jurisdictions. Where data is transferred outside the UK or EU, appropriate safeguards (such as Standard Contractual Clauses) are applied.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">7. Data retention</h2>
            <p>
              We retain your personal data only as long as necessary for the purposes described above:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Account data</strong> – retained while your account is active.</li>
              <li><strong>Transaction data</strong> – retained for at least 6 years for tax and accounting compliance.</li>
              <li><strong>Communication data</strong> – retained as long as relevant to your account history.</li>
              <li><strong>Location data</strong> – retained while your account is active, and replaced each time it is checked rather than kept as a history. Removed in full when you delete your account.</li>
            </ul>
            <p>
              You may request account deletion at any time. For detailed instructions on how to delete your account and what data will be removed, please see our <a href="/account-deletion" className="text-blue-600 hover:text-blue-700 hover:underline">Account Deletion page</a>. You can also delete specific data (such as posts, messages, connections, or profile information) directly in the app without deleting your entire account. See our <a href="/data-deletion" className="text-blue-600 hover:text-blue-700 hover:underline">Data Deletion page</a> for instructions on how to delete different types of data.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">8. Your rights</h2>
            <p>
              Under UK GDPR, you have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Access the personal data we hold about you.</li>
              <li>Request correction or deletion of your data. You can delete specific data types (such as messages, posts, connections, or profile information) directly in the app without deleting your entire account. See our <a href="/data-deletion" className="text-blue-600 hover:text-blue-700 hover:underline">Data Deletion page</a> for instructions.</li>
              <li>Withdraw consent for processing (where applicable).</li>
              <li>Object to or restrict certain processing.</li>
              <li>Data portability (transfer of your information).</li>
              <li>Lodge a complaint with the Information Commission (the ICO) if you believe your data rights have been violated.</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at <a href="mailto:contact@growth-sync.com" className="text-blue-600 hover:text-blue-700 hover:underline">contact@growth-sync.com</a>. We will respond to your request within one month.
            </p>
            <p>
              If you are unhappy with how we have handled your personal data, email us at <a href="mailto:contact@growth-sync.com" className="text-blue-600 hover:text-blue-700 hover:underline">contact@growth-sync.com</a> and put &ldquo;data complaint&rdquo; in the subject. We will acknowledge it within 30 days, look into it, keep you updated while we do, and tell you the outcome.
            </p>
            <p>
              You can also complain to the ICO at <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 hover:underline">https://ico.org.uk/make-a-complaint/</a> or by calling 0303 123 1113. You can do that at any time, and you do not have to come to us first.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">9. Security</h2>
            <p>
              We use encryption, secure data storage, and limited-access policies to protect your data. However, no system is entirely immune from risk, and users are encouraged to keep login credentials private.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">10. Children's privacy</h2>
            <p>
              GrowthSync is not intended for users under the age of 18. We do not knowingly collect personal data from children. If we become aware that we have collected data from a user under 18, we will take steps to delete that information promptly. If you believe we have collected information from a child, please contact us immediately.
            </p>
            <p className="mt-4">
              For comprehensive information about our child safety standards, zero tolerance policy for child sexual abuse and exploitation material (CSAM/CSAE), 
              and reporting procedures, please see our{" "}
              <a href="/child-safety" className="text-blue-600 hover:text-blue-700 hover:underline">Child Safety Standards (CSAE) page</a>.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">11. Cookies and similar technologies</h2>
            <p>
              We use cookies and similar technologies to enhance your experience. For detailed information about how we use cookies, please see our <a href="/cookies" className="text-blue-600 hover:text-blue-700 hover:underline">Cookie Policy</a>.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">12. Automated processing</h2>
            <p>
              GrowthSync does not send your data to any third-party artificial intelligence provider.
              Features that were previously powered by an external AI service, including the in-app
              assistant, profile rewriting and content suggestions, have been removed from the platform.
            </p>
            <p className="mt-4">
              Some features still work things out automatically, and all of them run on our own servers
              using only information you have already given us:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Your profile score, calculated from the fields you have filled in.</li>
              <li>Match scores and the reasons shown with them, calculated by comparing your profile with another member's.</li>
              <li>Safety checks on posts, comments and messages, which look for known words and patterns.</li>
              <li>Answers in support chat, matched against a written list of questions and answers.</li>
            </ul>
            <p className="mt-4">
              None of this involves a third party, none of it is used to train anything, and none of it
              leaves our systems. Deleting your account ends this processing in line with our retention
              and deletion practices (see Section 7 and our Account Deletion page).
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">13. Third-party links</h2>
            <p>
              Our app or website may contain links to other websites. We are not responsible for the privacy practices or content of those external sites.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">14. Updates to this policy</h2>
            <p>
              We may update this policy periodically. The latest version will always be available at <a href="/privacy" className="text-blue-600 hover:text-blue-700 hover:underline">https://growth-sync.com/privacy</a>. Users will be notified of any significant changes via the app or email.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">15. Contact and data protection</h2>
            <p>
              For any questions about this policy or how we handle your data, contact:
            </p>
            <p className="mt-2">
              Data Protection Contact: Thomas Cavies<br />
              Email: <a href="mailto:contact@growth-sync.com" className="text-blue-600 hover:text-blue-700 hover:underline">contact@growth-sync.com</a><br />
              Company: GrowthSync Network Ltd<br />
              Registered in England and Wales (Company No. 16795645)<br />
              Registered Office: Unit 6, Balfour Court, Leyland, PR25 2TF<br />
              ICO Registration Number: ZC025861
            </p>
          </section>
        </div>
    </LegalPageLayout>
  );
}
